'use client';

import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { clsx } from 'clsx';
import { useShallow } from 'zustand/react/shallow';
import AlertIcon from '../../icons/react/icon-alert';
import CheckIcon from '../../icons/react/icon-check';
import InfoIcon from '../../icons/react/icon-info';
import XLargeIcon from '../../icons/react/icon-x-large';
import { HorizontalStack } from '../alignment/horizontal-stack';
import { Button } from '../button/button';
import { type ToastDescription, type ToastVariant, useToast } from './toast-context';
import styles from './toast.module.scss';

const SLIDE_OUT_ANIMATION_TIME = 150;

export const toastIconByVariant: Record<ToastVariant, ReactNode> = {
  error: <AlertIcon aria-hidden height={16} />,
  info: <InfoIcon aria-hidden height={16} />,
  success: <CheckIcon aria-hidden height={16} />,
};

export function useIsShortTextToast() {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isShortText, setIsShortText] = useState(false);

  useLayoutEffect(() => {
    if (contentRef.current) {
      setIsShortText(contentRef.current.clientHeight < 32);
    }
  }, []);

  return [contentRef, isShortText] as const;
}

function Toast({ canDismiss, content, expiresAt, id, variant = 'success' }: ToastDescription) {
  const { removeToast } = useToast();
  const timerRef = useRef(0);
  const duration = expiresAt.valueOf() - Date.now();
  const [contentRef, isShortText] = useIsShortTextToast();
  const [isOpen, setIsOpen] = useState(duration > 0);

  useEffect(() => {
    if (!isOpen) {
      timerRef.current = window.setTimeout(() => {
        removeToast(id);
      }, SLIDE_OUT_ANIMATION_TIME);
    }

    return () => {
      window.clearTimeout(timerRef.current);
    };
  }, [isOpen, id, removeToast]);

  return (
    <ToastPrimitive.Root
      className={clsx(styles.toast, styles[`variant-${variant}`], canDismiss && styles.canDismiss)}
      data-testid={id}
      duration={duration}
      key={id}
      onOpenChange={setIsOpen}
      open={isOpen}
    >
      {/* {toast.title && <ToastPrimitive.ToastTitle>{title}</ToastPrimitive.ToastTitle>} */}
      <HorizontalStack blockAlign={isShortText ? 'center' : 'start'} gap={4} wrap="nowrap">
        <div className={styles.variantIcon}>{toastIconByVariant[variant]}</div>
        <ToastPrimitive.ToastDescription className={styles.content} ref={contentRef}>
          {content}
        </ToastPrimitive.ToastDescription>
        {canDismiss ? (
          <ToastPrimitive.Close aria-label="Dismiss notification" asChild className={styles.close}>
            <Button affixIcon={<XLargeIcon aria-hidden />} size="small" variant="light" />
          </ToastPrimitive.Close>
        ) : null}
      </HorizontalStack>
    </ToastPrimitive.Root>
  );
}

export function ToastRail() {
  const toasts = useToast(useShallow((state) => state.toasts));

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      <ToastPrimitive.Viewport className={styles.toastRail}>
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} />
        ))}
      </ToastPrimitive.Viewport>
    </ToastPrimitive.Provider>
  );
}
